// src/lib/guided/outcomes.ts
//
// Phase 2.4 of docs/guided-shopping_plan.md: "leaves become FormOutcomes
// with the offer's call to action" -- turning a FunnelConfig's own offers
// and `notFit` message into the `CompiledFunnel.outcomes` list
// `compileFunnel` (compile.ts) returns, and resolving a set of submitted
// answers to the one outcome they reach.
//
// Kept separate from compile.ts (which owns turning criteria into fields
// and steps): resolving an outcome needs none of that step-building
// machinery, only the same offer-survivor rule order.ts's tree uses
// (`survivorsForBucket`) and the synthetic "need" criterion compile.ts
// defines for the opener.
import type { FunnelConfig, FunnelOffer, FunnelOfferAction } from "./config";
import type { FormOutcome, FormOutcomeCta } from "@/lib/types";
import { survivorsForBucket } from "./order";
import {
  NEED_CRITERION_ID,
  NOT_SURE_YET_OPTION,
  PREFER_TO_DISCUSS_OPTION,
  effectiveOfferFit,
} from "./compile";

/** Id of the polite-no outcome every compiled flow declares alongside its offers. */
export const POLITE_NO_OUTCOME_ID = "not-a-fit";

/**
 * `quote` gets no CTA: there is nothing to link to yet, and the result
 * screen (Phase 5) asks for contact details instead of pointing anywhere.
 * The others map to the one URL/label their action already carries.
 */
function ctaForOfferAction(
  action: FunnelOfferAction,
): FormOutcomeCta | undefined {
  switch (action.type) {
    case "book":
      return { label: "Book a call", url: action.url };
    case "guide":
      return { label: "Get the guide", url: action.url };
    case "refer":
      return { label: action.text, url: action.url };
    case "quote":
      return undefined;
    default: {
      const exhaustive: never = action;
      return exhaustive;
    }
  }
}

function outcomeForOffer(offer: FunnelOffer): FormOutcome {
  return {
    id: offer.id,
    title: offer.name,
    body: offer.description,
    cta: ctaForOfferAction(offer.action),
  };
}

function politeNoOutcome(config: FunnelConfig): FormOutcome {
  return {
    id: POLITE_NO_OUTCOME_ID,
    title: "Not a fit right now",
    body: config.notFit.text,
    cta: config.notFit.resourceUrl
      ? {
          label: "Here's a resource that might help",
          url: config.notFit.resourceUrl,
        }
      : undefined,
    isDefault: true,
  };
}

/** Every outcome a compiled flow can end on: one per offer, plus the polite no. */
export function buildFunnelOutcomes(config: FunnelConfig): FormOutcome[] {
  return [...config.offers.map(outcomeForOffer), politeNoOutcome(config)];
}

/**
 * "Not sure yet" / "Prefer to discuss" are synthetic hedge options
 * (compile.ts's `optionsForCriterion`), not part of any owner's `offerFit`
 * bucket list. Reading one as an unaccepted bucket would eliminate every
 * offer that cares about the criterion, rejecting a visitor who never
 * actually ruled anything out.
 */
function isSyntheticBucketValue(value: string): boolean {
  return (
    value === String(NOT_SURE_YET_OPTION.value) ||
    value === String(PREFER_TO_DISCUSS_OPTION.value)
  );
}

/**
 * Which outcome a set of answers reaches: the offer surviving every
 * answered criterion's `offerFit` (the synthetic "need" answer included,
 * via the same merged `effectiveOfferFit` `compile.ts` uses to build the
 * opener), or the polite no when zero or more than one still do. A later
 * gate can still rule out an offer the "need" opener already pointed at --
 * survivors just drop to zero.
 */
export function resolveOutcome(
  config: FunnelConfig,
  answers: Record<string, unknown>,
): FormOutcome {
  const offerFit = effectiveOfferFit(config);
  let remaining = config.offers.map((offer) => offer.id);

  const criterionIds = [
    ...(config.offers.length > 1 ? [NEED_CRITERION_ID] : []),
    ...config.criteria.map((criterion) => criterion.id),
  ];

  for (const criterionId of criterionIds) {
    const value = answers[criterionId];
    if (typeof value !== "string" || value === "") continue;
    if (isSyntheticBucketValue(value)) continue;
    remaining = survivorsForBucket(criterionId, value, remaining, offerFit);
  }

  if (remaining.length === 1) {
    const offer = config.offers.find((o) => o.id === remaining[0]);
    if (offer) return outcomeForOffer(offer);
  }
  return politeNoOutcome(config);
}
